import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

type ChartHistory = {
  t: number[];
  o: number[];
  h: number[];
  l: number[];
  c: number[];
  v: (number | null)[];
};
type LastBar = {
  time: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume?: number | null;
};

type MiniChartVM = {
  symbol: string | null;
  priceSource: 'traded' | 'mark' | 'spot';
  lastFuturesPriceSource: 'traded' | 'mark';
  resolution: string;
  loading: boolean;
  history: ChartHistory | null;
  lastBar: LastBar | null;
  initialLoadComplete: boolean;
  hasMore: boolean;
};

type MiniChartStore = {
  state: MiniChartVM;
  actions: {
    setSettings: (
      s: Partial<
        Pick<
          MiniChartVM,
          'symbol' | 'priceSource' | 'resolution' | 'lastFuturesPriceSource'
        >
      >
    ) => void;
    setLoading: (loading: boolean) => void;
    setHistory: (args: {
      symbol: string;
      resolution: string;
      result: ChartHistory;
    }) => void;
    updateLastBar: (symbol: string, resolution: string, bar: LastBar) => void;
    prependHistory: (args: { result: ChartHistory }) => void;
    reset: () => void;
    setHasMore: (hasMore: boolean) => void;
    resetHistory: () => void;
  };
};

const initial: MiniChartVM = {
  symbol: null,
  priceSource: 'mark',
  lastFuturesPriceSource: 'mark',
  resolution: '1',
  loading: false,
  history: null,
  lastBar: null,
  initialLoadComplete: false,
  hasMore: true,
};

const useMiniChartStoreBase = create<MiniChartStore>()(
  devtools(
    set => ({
      state: initial,
      actions: {
        setSettings: s =>
          set(
            store => ({ state: { ...store.state, ...s } }),
            false,
            'miniChart/setSettings'
          ),
        setLoading: loading =>
          set(
            store => ({ state: { ...store.state, loading } }),
            false,
            'miniChart/setLoading'
          ),
        setHistory: ({ result }) =>
          set(
            store => ({
              state: {
                ...store.state,
                history: result,
                loading: false,
                initialLoadComplete: true,
              },
            }),
            false,
            'miniChart/setHistory'
          ),
        prependHistory: ({ result }) =>
          set(
            store => {
              const prev = store.state.history ?? {
                t: [],
                o: [],
                h: [],
                l: [],
                c: [],
                v: [],
              };
              const seen = new Set(prev.t);
              const older = { t: [], o: [], h: [], l: [], c: [], v: [] } as ChartHistory;
              result.t.forEach((t, i) => {
                if (!seen.has(t)) {
                  older.t.push(t);
                  older.o.push(Number(result.o[i]));
                  older.h.push(Number(result.h[i]));
                  older.l.push(Number(result.l[i]));
                  older.c.push(Number(result.c[i]));
                  older.v.push(result.v[i] ?? null);
                }
              });
              const merged: ChartHistory = {
                t: [...older.t, ...prev.t],
                o: [...older.o, ...prev.o],
                h: [...older.h, ...prev.h],
                l: [...older.l, ...prev.l],
                c: [...older.c, ...prev.c],
                v: [...older.v, ...prev.v],
              };
              return { state: { ...store.state, history: merged, loading: false } };
            },
            false,
            'miniChart/prependHistory'
          ),
        updateLastBar: (_symbol, _res, bar) =>
          set(
            store => ({ state: { ...store.state, lastBar: bar } }),
            false,
            'miniChart/updateLastBar'
          ),
        setHasMore: hasMore =>
          set(
            store => ({ state: { ...store.state, hasMore } }),
            false,
            'miniChart/setHasMore'
          ),
        reset: () => set({ state: initial }, false, 'miniChart/reset'),
        resetHistory: () =>
          set(
            store => ({
              state: {
                ...store.state,
                history: null,
                lastBar: null,
                loading: false,
                initialLoadComplete: false,
                hasMore: true,
              },
            }),
            false,
            'miniChart/resetHistory'
          ),
      },
    }),
    { name: 'MiniChartStore' }
  )
);

export { useMiniChartStoreBase };
